// ═══════════════════════════════════════════════════════════════
// Common Operating Picture (COP) — Live multi-module dashboard
// Draggable grid with CSS-based charts (no Recharts/ResponsiveContainer)
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useCallback } from 'react';
import { ResponsiveGridLayout as RGLBase } from 'react-grid-layout';
const RGLComponent = RGLBase as any;
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {
  Title, Text, Paper, Group, Button, Stack, SimpleGrid,
  Progress, ActionIcon, Menu, Tooltip, RingProgress, ThemeIcon,
} from '../../components/gab/GabComponents';
import {
  IconSettings, IconPlus, IconTrash, IconGripVertical,
  IconBuildingCommunity, IconCash, IconBackhoe,
  IconAlertTriangle, IconClipboardCheck, IconHeartHandshake,
  IconClock, IconRefresh, IconMaximize, IconLayoutDashboard,
  IconBuildingStore, IconUserPlus, IconReceipt, IconChartBar,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useAuthStore } from '../../store/authStore';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useShelterStore } from '../../store/shelterStore';
import { useImpactStore } from '../../store/impactStore';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useVolunteerStore } from '../../store/volunteerStore';
import { useHandoffStore } from '../../store/handoffStore';
import { useRollCallStore } from '../../store/rollcallStore';
import { useMainStreetStore } from '../../store/mainstreetStore';
import { useIntakeStore } from '../../store/intakeStore';
import { usePolling } from '../../hooks/usePolling';
import { fmtDateTime } from '../../lib/dateFormat';

// ── Types ────────────────────────────────────────────────────

interface LayoutItem { i: string; x: number; y: number; w: number; h: number; minW?: number; minH?: number; }

type ChartMode = 'stat' | 'bar' | 'donut';

interface WidgetDef {
  id: string; title: string; icon: React.ComponentType<{ size?: number }>; color: string;
  defaultW: number; defaultH: number; modes: ChartMode[];
}

const WIDGETS: WidgetDef[] = [
  { id: 'incident_stats', title: 'Incident Overview', icon: IconLayoutDashboard, color: 'var(--color-primary-700)', defaultW: 12, defaultH: 2, modes: ['stat'] },
  { id: 'shelter_occupancy', title: 'Shelter Occupancy', icon: IconBuildingCommunity, color: '#0284c7', defaultW: 4, defaultH: 4, modes: ['stat', 'bar'] },
  { id: 'intake_triage', title: 'Intake Triage', icon: IconUserPlus, color: '#b91c1c', defaultW: 4, defaultH: 3, modes: ['stat', 'donut'] },
  { id: 'business_status', title: 'Business Status', icon: IconBuildingStore, color: '#7c2d12', defaultW: 4, defaultH: 3, modes: ['stat', 'bar', 'donut'] },
  { id: 'pa_pipeline', title: 'PA Phase Pipeline', icon: IconCash, color: '#1E40AF', defaultW: 4, defaultH: 3, modes: ['stat', 'bar', 'donut'] },
  { id: 'pa_financial', title: 'PA Financial Summary', icon: IconCash, color: '#059669', defaultW: 4, defaultH: 3, modes: ['stat', 'bar'] },
  { id: 'pa_rfr', title: 'Reimbursement Status', icon: IconReceipt, color: '#0e7490', defaultW: 4, defaultH: 3, modes: ['stat', 'donut'] },
  { id: 'debris_progress', title: 'Debris Progress', icon: IconBackhoe, color: '#d97706', defaultW: 4, defaultH: 3, modes: ['stat', 'donut'] },
  { id: 'impact_summary', title: 'Impact Assessments', icon: IconAlertTriangle, color: '#dc2626', defaultW: 4, defaultH: 3, modes: ['stat', 'bar', 'donut'] },
  { id: 'volunteers', title: 'Volunteer Status', icon: IconHeartHandshake, color: '#7c3aed', defaultW: 4, defaultH: 3, modes: ['stat', 'donut'] },
  { id: 'rollcall', title: 'RollCall Status', icon: IconClipboardCheck, color: '#0d9488', defaultW: 4, defaultH: 3, modes: ['stat'] },
  { id: 'activity_feed', title: 'Recent Activity', icon: IconClock, color: '#6b7280', defaultW: 4, defaultH: 5, modes: ['stat'] },
];

const LS_KEY = 'cop-layout-v2';
const LS_WIDGETS = 'cop-active-widgets-v2';
const LS_MODES = 'cop-modes-v2';
const DEFAULT_WIDGETS = ['incident_stats', 'shelter_occupancy', 'intake_triage', 'business_status', 'pa_pipeline', 'pa_financial', 'debris_progress', 'impact_summary', 'pa_rfr', 'volunteers'];
const MODE_LABELS: Record<ChartMode, string> = { stat: 'Numbers', bar: 'Bar Chart', donut: 'Donut Chart' };

function loadJson<T>(key: string, fb: T): T { try { const r = localStorage.getItem(key); return r ? JSON.parse(r) : fb; } catch { return fb; } }
const save = (k: string, v: unknown) => localStorage.setItem(k, JSON.stringify(v));

// ── CSS Bar Chart ────────────────────────────────────────────

function CSSBar({ data }: { data: Array<{ label: string; value: number; color: string }> }) {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <Stack gap={4} style={{ flex: 1, overflow: 'auto' }}>
      {data.map(d => (
        <div key={d.label}>
          <Group justify="space-between" mb={1}>
            <Text size="xs" lineClamp={1} style={{ flex: 1 }}>{d.label}</Text>
            <Text size="xs" fw={700}>{d.value}</Text>
          </Group>
          <div style={{ height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ width: `${(d.value / max) * 100}%`, height: '100%', background: d.color, borderRadius: 3, transition: 'width 0.3s' }} />
          </div>
        </div>
      ))}
    </Stack>
  );
}

function CSSDonut({ data, size = 80 }: { data: Array<{ label: string; value: number; color: string }>; size?: number }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <Text size="xs" c="dimmed">No data</Text>;
  let cumPct = 0;
  const segments = data.filter(d => d.value > 0).map(d => {
    const pct = (d.value / total) * 100;
    const seg = { ...d, pct, start: cumPct };
    cumPct += pct;
    return seg;
  });
  const gradient = segments.map(s => `${s.color} ${s.start}% ${s.start + s.pct}%`).join(', ');

  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
      <div style={{
        width: size, height: size, borderRadius: '50%', flexShrink: 0,
        background: `conic-gradient(${gradient})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ width: size * 0.6, height: size * 0.6, borderRadius: '50%', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Text size="xs" fw={700}>{total}</Text>
        </div>
      </div>
      <Stack gap={2} style={{ flex: 1, overflow: 'auto' }}>
        {segments.map(s => (
          <Group key={s.label} gap={4}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: s.color, flexShrink: 0 }} />
            <Text size="xs" lineClamp={1} style={{ flex: 1 }}>{s.label}</Text>
            <Text size="xs" fw={600}>{s.value}</Text>
          </Group>
        ))}
      </Stack>
    </div>
  );
}

// ── Widgets ──────────────────────────────────────────────────

function W_IncidentStats() {
  const activeIncident = useAuthStore(s => s.activeIncident);
  const shelters = useShelterStore(s => s.shelters);
  const projects = usePAGrantsStore(s => s.projects);
  const assessments = useImpactStore(s => s.assessments);
  const loadTickets = useDebrisOpsStore(s => s.loadTickets);
  const volunteers = useVolunteerStore(s => s.volunteers);
  const incId = activeIncident?.id ? String(activeIncident.id) : '';
  const stats = [
    { label: 'Open Shelters', value: (incId ? shelters.filter(s => s.incidentId === incId) : shelters).filter(s => s.status === 'open').length, color: '#0284c7', icon: IconBuildingCommunity },
    { label: 'PA Projects', value: projects.filter(p => p.workType !== 'hmgp').length, color: '#1E40AF', icon: IconCash },
    { label: 'Assessments', value: (incId ? assessments.filter(a => a.incidentId === incId) : assessments).length, color: '#dc2626', icon: IconAlertTriangle },
    { label: 'Debris Tickets', value: (incId ? loadTickets.filter(t => t.incidentId === incId) : loadTickets).length, color: '#d97706', icon: IconBackhoe },
    { label: 'Volunteers', value: volunteers.filter(v => v.status === 'active').length, color: '#7c3aed', icon: IconHeartHandshake },
  ];
  return (
    <Group justify="space-around" h="100%" align="center" gap="xl">
      {stats.map(s => (
        <Group key={s.label} gap="xs" align="center">
          <ThemeIcon size="lg" variant="light" color={s.color} radius="md"><s.icon size={18} /></ThemeIcon>
          <div><Text size="xl" fw={800} lh={1} style={{ color: s.color }}>{s.value}</Text><Text size="xs" c="dimmed">{s.label}</Text></div>
        </Group>
      ))}
    </Group>
  );
}

function W_Shelter({ mode = 'stat' }: { mode?: ChartMode }) {
  const shelters = useShelterStore(s => s.shelters);
  const activeIncident = useAuthStore(s => s.activeIncident);
  const incId = activeIncident?.id ? String(activeIncident.id) : '';
  const iSh = incId ? shelters.filter(s => s.incidentId === incId) : shelters;
  const totalCap = iSh.reduce((s, sh) => s + sh.capacity, 0);
  const totalPop = iSh.reduce((s, sh) => s + sh.currentPopulation, 0);
  const pct = totalCap > 0 ? Math.round((totalPop / totalCap) * 100) : 0;
  const barData = iSh.map(s => ({ label: s.name || 'Unnamed', value: s.currentPopulation, color: s.currentPopulation >= s.capacity * 0.9 ? '#dc2626' : s.currentPopulation >= s.capacity * 0.7 ? '#f97316' : '#22c55e' }));
  if (mode === 'bar') return <Stack gap="xs" h="100%"><Text size="xl" fw={800}>{iSh.filter(s => s.status === 'open').length} <Text span size="sm" c="dimmed">open</Text></Text><CSSBar data={barData} /></Stack>;
  return (
    <Stack gap="xs" h="100%">
      <Group justify="space-between">
        <div><Text size="xl" fw={800}>{iSh.filter(s => s.status === 'open').length}</Text><Text size="xs" c="dimmed">shelters open</Text></div>
        <RingProgress size={56} thickness={6} sections={[{ value: pct, color: pct > 80 ? 'red' : pct > 50 ? 'orange' : 'green' }]}
          label={<Text size="xs" ta="center" fw={700}>{pct}%</Text>} />
      </Group>
      <Text size="xs" c="dimmed">{totalPop.toLocaleString()} / {totalCap.toLocaleString()} capacity</Text>
      <Stack gap={3} style={{ flex: 1, overflow: 'auto' }}>
        {iSh.map(s => {
          const sp = s.capacity > 0 ? Math.round((s.currentPopulation / s.capacity) * 100) : 0;
          return (<div key={s.id}><Group justify="space-between"><Text size="xs" fw={500} lineClamp={1}>{s.name}</Text><Text size="xs" c="dimmed">{s.currentPopulation}/{s.capacity}</Text></Group>
            <Progress value={sp} size={4} color={sp >= 90 ? 'red' : sp >= 70 ? 'orange' : 'green'} /></div>);
        })}
      </Stack>
    </Stack>
  );
}

function W_IntakeTriage({ mode = 'stat' }: { mode?: ChartMode }) {
  const individuals = useIntakeStore(s => s.individuals);
  const activeIncident = useAuthStore(s => s.activeIncident);
  const incId = activeIncident?.id ? String(activeIncident.id) : '';
  const filtered = incId ? individuals.filter(i => i.incidentId === incId) : individuals;
  const active = filtered.filter(i => i.status === 'active');
  const r = active.filter(i => i.triage === 'red').length, y = active.filter(i => i.triage === 'yellow').length, g = active.filter(i => i.triage === 'green').length;
  const triageData = [{ label: 'Red', value: r, color: '#dc2626' }, { label: 'Yellow', value: y, color: '#eab308' }, { label: 'Green', value: g, color: '#22c55e' }];
  if (mode === 'donut') return <Stack gap="xs" h="100%"><Text size="xl" fw={800}>{active.length} <Text span size="sm" c="dimmed">active</Text></Text><CSSDonut data={triageData} /><Text size="xs" c="dimmed">{filtered.length} total</Text></Stack>;
  return (
    <Stack gap="sm" h="100%" justify="center">
      <Text size="xl" fw={800}>{active.length} <Text span size="sm" c="dimmed">active</Text></Text>
      <SimpleGrid cols={3}>
        <Paper p="xs" radius="sm" ta="center" style={{ background: '#fef2f2', border: '1px solid #fecaca' }}><Text size="xl" fw={800} c="red">{r}</Text><Text size="xs" c="red.7">Red</Text></Paper>
        <Paper p="xs" radius="sm" ta="center" style={{ background: '#fefce8', border: '1px solid #fef08a' }}><Text size="xl" fw={800} style={{ color: '#a16207' }}>{y}</Text><Text size="xs" style={{ color: '#a16207' }}>Yellow</Text></Paper>
        <Paper p="xs" radius="sm" ta="center" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}><Text size="xl" fw={800} c="green">{g}</Text><Text size="xs" c="green.7">Green</Text></Paper>
      </SimpleGrid>
      <Text size="xs" c="dimmed">{filtered.length} total ({filtered.filter(i => i.status === 'discharged').length} discharged)</Text>
    </Stack>
  );
}

function W_Business({ mode = 'stat' }: { mode?: ChartMode }) {
  const organizations = useMainStreetStore(s => s.organizations);
  const statuses: Record<string, number> = {};
  organizations.forEach(o => { statuses[o.opStatus || 'unknown'] = (statuses[o.opStatus || 'unknown'] || 0) + 1; });
  const colors: Record<string, string> = { open: '#22c55e', closed: '#dc2626', damaged: '#f97316', limited: '#eab308', pending: '#8b5cf6', unknown: '#6b7280' };
  const data = Object.entries(statuses).sort((a, b) => b[1] - a[1]).map(([k, v]) => ({ label: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), value: v, color: colors[k] || '#6b7280' }));
  return (
    <Stack gap="xs" h="100%">
      <Text size="xl" fw={800}>{organizations.length} <Text span size="sm" c="dimmed">businesses</Text></Text>
      {mode === 'bar' ? <CSSBar data={data} /> : mode === 'donut' ? <CSSDonut data={data} /> :
        <Stack gap={2} style={{ flex: 1 }}>{data.map(d => (<Group key={d.label} justify="space-between"><Group gap={4}><div style={{ width: 8, height: 8, borderRadius: '50%', background: d.color, flexShrink: 0 }} /><Text size="xs">{d.label}</Text></Group><Text size="xs" fw={700}>{d.value}</Text></Group>))}</Stack>}
    </Stack>
  );
}

function W_PAPipeline({ mode = 'stat' }: { mode?: ChartMode }) {
  const projects = usePAGrantsStore(s => s.projects);
  const pa = projects.filter(p => p.workType !== 'hmgp');
  const phases: Record<string, number> = {};
  pa.forEach(p => { phases[p.phase] = (phases[p.phase] || 0) + 1; });
  const labels: Record<string, string> = { formulation: 'Formulation', review: 'Review', obligation: 'Obligation', work_in_progress: 'WIP', closeout: 'Closeout' };
  const colors: Record<string, string> = { formulation: '#3b82f6', review: '#eab308', obligation: '#22c55e', work_in_progress: '#f97316', closeout: '#8b5cf6' };
  const data = Object.entries(labels).map(([k, l]) => ({ label: l, value: phases[k] || 0, color: colors[k] || '#6b7280' }));
  return (
    <Stack gap="xs" h="100%">
      <Text size="xl" fw={800}>{pa.length} <Text span size="sm" c="dimmed">projects</Text></Text>
      {mode === 'donut' ? <CSSDonut data={data} /> : mode === 'bar' ? <CSSBar data={data} /> :
        <Stack gap={2} style={{ flex: 1 }}>{data.map(d => (<Group key={d.label} justify="space-between"><Group gap={4}><div style={{ width: 8, height: 8, borderRadius: 2, background: d.color }} /><Text size="xs">{d.label}</Text></Group><Text size="xs" fw={700}>{d.value}</Text></Group>))}</Stack>}
    </Stack>
  );
}

function W_PAFinancial({ mode = 'stat' }: { mode?: ChartMode }) {
  const projects = usePAGrantsStore(s => s.projects);
  const pa = projects.filter(p => p.workType !== 'hmgp');
  const est = pa.reduce((s, p) => s + (parseFloat(p.estimatedCost) || 0), 0);
  const obl = pa.reduce((s, p) => s + (parseFloat(p.obligatedAmount) || 0), 0);
  const drawn = pa.reduce((s, p) => s + (parseFloat(p.drawdownAmount) || 0), 0);
  const fmt = (n: number) => '$' + (n >= 1e6 ? (n / 1e6).toFixed(1) + 'M' : n >= 1000 ? (n / 1000).toFixed(0) + 'K' : n.toFixed(0));
  const barData = [{ label: 'Estimated', value: est, color: '#6b7280' }, { label: 'Obligated', value: obl, color: '#059669' }, { label: 'Drawn Down', value: drawn, color: '#2563EB' }];
  if (mode === 'bar') return <Stack gap="xs" h="100%"><CSSBar data={barData} /></Stack>;
  return (
    <Stack gap="sm" h="100%" justify="center">
      {barData.map(i => (
        <div key={i.label}><Group justify="space-between"><Text size="xs" c="dimmed">{i.label}</Text><Text size="sm" fw={700} style={{ color: i.color }}>{fmt(i.value)}</Text></Group>
          {est > 0 && <Progress value={(i.value / est) * 100} size={4} color={i.color} mt={2} />}</div>
      ))}
    </Stack>
  );
}

function W_PARFR({ mode = 'stat' }: { mode?: ChartMode }) {
  const rfrs = usePAGrantsStore(s => s.reimbursementRequests);
  const totReq = rfrs.reduce((s, r) => s + (parseFloat(r.requestedAmount) || 0), 0);
  const totAppr = rfrs.reduce((s, r) => s + (parseFloat(r.approvedAmount) || 0), 0);
  const fmt = (n: number) => '$' + (n >= 1e6 ? (n / 1e6).toFixed(1) + 'M' : n >= 1000 ? (n / 1000).toFixed(0) + 'K' : n.toFixed(0));
  const statuses: Record<string, number> = {};
  rfrs.forEach(r => { statuses[r.status] = (statuses[r.status] || 0) + 1; });
  const colors: Record<string, string> = { draft: '#6b7280', submitted: '#3b82f6', under_review: '#8b5cf6', approved: '#22c55e', paid: '#0d9488', denied: '#dc2626', revision_needed: '#f97316' };
  const data = Object.entries(statuses).map(([k, v]) => ({ label: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), value: v, color: colors[k] || '#6b7280' }));
  if (mode === 'donut') return <Stack gap="xs" h="100%"><Text size="xl" fw={800}>{rfrs.length} <Text span size="sm" c="dimmed">RFRs</Text></Text><CSSDonut data={data} /></Stack>;
  return (
    <Stack gap="sm" h="100%">
      <Text size="xl" fw={800}>{rfrs.length} <Text span size="sm" c="dimmed">RFRs</Text></Text>
      <Group justify="space-between"><Text size="xs" c="dimmed">Requested</Text><Text size="sm" fw={700}>{fmt(totReq)}</Text></Group>
      <Group justify="space-between"><Text size="xs" c="dimmed">Approved</Text><Text size="sm" fw={700} c="green">{fmt(totAppr)}</Text></Group>
      <CSSDonut data={data} size={60} />
    </Stack>
  );
}

function W_Debris({ mode = 'stat' }: { mode?: ChartMode }) {
  const tix = useDebrisOpsStore(s => s.loadTickets);
  const activeIncident = useAuthStore(s => s.activeIncident);
  const incId = activeIncident?.id ? String(activeIncident.id) : '';
  const tickets = incId ? tix.filter(t => t.incidentId === incId) : tix;
  const verified = tickets.filter(t => t.status === 'verified' || t.status === 'delivered').length;
  const pending = tickets.filter(t => t.status === 'draft' || t.status === 'in_transit' || t.status === 'at_site').length;
  const totalCY = tickets.reduce((s, t) => s + (parseFloat(t.calculatedCubicYards) || 0), 0);
  const byType: Record<string, number> = {};
  tickets.forEach(t => { byType[t.debrisType || 'unknown'] = (byType[t.debrisType || 'unknown'] || 0) + 1; });
  const typeColors: Record<string, string> = { vegetative: '#22c55e', c_and_d: '#f97316', mixed: '#8b5cf6', hazardous: '#dc2626', unknown: '#6b7280' };
  const typeData = Object.entries(byType).map(([k, v]) => ({ label: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), value: v, color: typeColors[k] || '#6b7280' }));
  if (mode === 'donut') return <Stack gap="xs" h="100%"><Text size="xl" fw={800}>{tickets.length} <Text span size="sm" c="dimmed">tickets</Text></Text><CSSDonut data={typeData} /><Text size="xs" c="dimmed">{totalCY.toLocaleString(undefined, { maximumFractionDigits: 0 })} CY total</Text></Stack>;
  return (
    <Stack gap="sm" h="100%">
      <Text size="xl" fw={800}>{tickets.length} <Text span size="sm" c="dimmed">tickets</Text></Text>
      <SimpleGrid cols={2}>
        <Paper p="xs" bg="green.0" radius="sm" ta="center"><Text size="lg" fw={700} c="green">{verified}</Text><Text size="xs" c="dimmed">Verified</Text></Paper>
        <Paper p="xs" bg="orange.0" radius="sm" ta="center"><Text size="lg" fw={700} c="orange">{pending}</Text><Text size="xs" c="dimmed">Pending</Text></Paper>
      </SimpleGrid>
      <div><Text size="xs" c="dimmed">Total CY</Text><Text size="lg" fw={800}>{totalCY.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Text></div>
      {typeData.length > 1 && <CSSDonut data={typeData} size={50} />}
    </Stack>
  );
}

function W_Impact({ mode = 'stat' }: { mode?: ChartMode }) {
  const assessments = useImpactStore(s => s.assessments);
  const activeIncident = useAuthStore(s => s.activeIncident);
  const incId = activeIncident?.id ? String(activeIncident.id) : '';
  const ia = incId ? assessments.filter(a => a.incidentId === incId) : assessments;
  const byCat: Record<string, number> = {};
  ia.forEach(a => { byCat[a.damageCategory || 'unknown'] = (byCat[a.damageCategory || 'unknown'] || 0) + 1; });
  const catColors: Record<string, string> = { destroyed: '#dc2626', major: '#f97316', minor: '#eab308', affected: '#3b82f6', inaccessible: '#6b7280', unknown: '#9ca3af' };
  const data = Object.entries(byCat).sort((a, b) => b[1] - a[1]).map(([k, v]) => ({ label: k.replace(/\b\w/g, c => c.toUpperCase()), value: v, color: catColors[k] || '#6b7280' }));
  if (mode === 'donut') return <Stack gap="xs" h="100%"><Text size="xl" fw={800}>{ia.length} <Text span size="sm" c="dimmed">assessments</Text></Text><CSSDonut data={data} /></Stack>;
  if (mode === 'bar') return <Stack gap="xs" h="100%"><Text size="xl" fw={800}>{ia.length} <Text span size="sm" c="dimmed">assessments</Text></Text><CSSBar data={data} /></Stack>;
  return (
    <Stack gap="xs" h="100%">
      <Text size="xl" fw={800}>{ia.length} <Text span size="sm" c="dimmed">assessments</Text></Text>
      <Stack gap={2} style={{ flex: 1 }}>{data.map(d => (<Group key={d.label} justify="space-between"><Group gap={4}><div style={{ width: 8, height: 8, borderRadius: 2, background: d.color }} /><Text size="xs">{d.label}</Text></Group><Text size="xs" fw={700}>{d.value}</Text></Group>))}</Stack>
    </Stack>
  );
}

function W_Volunteers({ mode = 'stat' }: { mode?: ChartMode }) {
  const volunteers = useVolunteerStore(s => s.volunteers);
  const assignments = useVolunteerStore(s => s.assignments);
  const active = volunteers.filter(v => v.status === 'active').length;
  const deployed = assignments.filter(a => a.status === 'confirmed' || a.status === 'scheduled').length;
  const completed = assignments.filter(a => a.status === 'completed').length;
  const inactive = volunteers.length - active;
  const volData = [{ label: 'Active', value: active, color: '#22c55e' }, { label: 'Deployed', value: deployed, color: '#3b82f6' }, { label: 'Completed', value: completed, color: '#0d9488' }, { label: 'Inactive', value: inactive, color: '#6b7280' }];
  if (mode === 'donut') return <Stack gap="xs" h="100%"><Text size="xl" fw={800}>{volunteers.length} <Text span size="sm" c="dimmed">volunteers</Text></Text><CSSDonut data={volData} /></Stack>;
  return (
    <Stack gap="sm" h="100%" justify="center">
      <Text size="xl" fw={800}>{volunteers.length} <Text span size="sm" c="dimmed">total</Text></Text>
      <SimpleGrid cols={3}>
        <div><Text size="lg" fw={700} c="green">{active}</Text><Text size="xs" c="dimmed">Active</Text></div>
        <div><Text size="lg" fw={700} c="blue">{deployed}</Text><Text size="xs" c="dimmed">Deployed</Text></div>
        <div><Text size="lg" fw={700} c="teal">{completed}</Text><Text size="xs" c="dimmed">Done</Text></div>
      </SimpleGrid>
    </Stack>
  );
}

function W_RollCall() {
  const events = useRollCallStore(s => s.events);
  const checkins = useRollCallStore(s => s.checkins);
  const openEvents = events.filter(e => e.eventStatus === 'open').length;
  const currentlyIn = checkins.filter(c => c.checkInTime && !c.checkOutTime).length;
  return (
    <Stack gap="sm" h="100%" justify="center">
      <SimpleGrid cols={2}>
        <Paper p="xs" bg="blue.0" radius="sm" ta="center"><Text size="lg" fw={700} c="blue">{openEvents}</Text><Text size="xs" c="dimmed">Open Events</Text></Paper>
        <Paper p="xs" bg="green.0" radius="sm" ta="center"><Text size="lg" fw={700} c="green">{currentlyIn}</Text><Text size="xs" c="dimmed">Checked In</Text></Paper>
      </SimpleGrid>
      <Text size="xs" c="dimmed">{checkins.length} total check-ins</Text>
    </Stack>
  );
}

function W_Activity() {
  const notes = useHandoffStore(s => s.notes);
  const sorted = useMemo(() => [...notes].sort((a, b) => (b.datecreated || '').localeCompare(a.datecreated || '')).slice(0, 10), [notes]);
  return (
    <Stack gap={4} h="100%" style={{ overflow: 'auto' }}>
      {sorted.length === 0 && <Text size="xs" c="dimmed" ta="center" py="md">No recent activity</Text>}
      {sorted.map(n => (
        <Paper key={n.id} p={6} radius="sm" style={{ background: 'var(--mantine-color-gray-0)' }}>
          <Text size="xs" fw={500} lineClamp={1}>{n.positionName || 'Handoff'} — {n.fromShift} to {n.toShift}</Text>
          <Text size="xs" c="dimmed">{n.senderName || '—'} · {fmtDateTime(n.datecreated)}</Text>
        </Paper>
      ))}
    </Stack>
  );
}

const WIDGET_MAP: Record<string, React.ComponentType<{ mode?: ChartMode }>> = {
  incident_stats: W_IncidentStats, shelter_occupancy: W_Shelter, intake_triage: W_IntakeTriage,
  business_status: W_Business, pa_pipeline: W_PAPipeline, pa_financial: W_PAFinancial,
  pa_rfr: W_PARFR, debris_progress: W_Debris, impact_summary: W_Impact,
  volunteers: W_Volunteers, rollcall: W_RollCall, activity_feed: W_Activity,
};

// ── Main COP ─────────────────────────────────────────────────

export default function COP() {
  const activeIncident = useAuthStore(s => s.activeIncident);
  const [activeWidgets, setActiveWidgets] = useState<string[]>(() => loadJson(LS_WIDGETS, DEFAULT_WIDGETS));
  const [isEditing, setIsEditing] = useState(false);
  const [widgetModes, setWidgetModes] = useState<Record<string, ChartMode>>(() => loadJson(LS_MODES, {}));

  const setMode = (id: string, mode: ChartMode) => {
    const next = { ...widgetModes, [id]: mode };
    setWidgetModes(next); save(LS_MODES, next);
  };

  const loadAllData = useCallback(() => {
    useShelterStore.getState().loadShelters();
    usePAGrantsStore.getState().fetchProjects();
    usePAGrantsStore.getState().fetchOrganizations();
    usePAGrantsStore.getState().fetchReimbursements();
    useImpactStore.getState().loadAssessments();
    useDebrisOpsStore.getState().loadLoadTickets();
    useVolunteerStore.getState().loadVolunteers();
    useVolunteerStore.getState().loadAssignments();
    useHandoffStore.getState().loadNotes();
    useRollCallStore.getState().loadAll();
    useMainStreetStore.getState().loadOrganizations();
    if (activeIncident?.id) useIntakeStore.getState().loadIndividuals(String(activeIncident.id));
  }, [activeIncident?.id]);

  useEffect(() => { loadAllData(); }, [loadAllData]);
  usePolling(loadAllData, 30000);

  const defaultLayout = useMemo((): LayoutItem[] => {
    let x = 0, y = 0;
    return activeWidgets.map(id => {
      const def = WIDGETS.find(w => w.id === id);
      const w = def?.defaultW || 4, h = def?.defaultH || 3;
      if (x + w > 12) { x = 0; y += h; }
      const item: LayoutItem = { i: id, x, y, w, h, minW: 3, minH: 2 };
      x += w; return item;
    });
  }, [activeWidgets]);

  const [layouts, setLayouts] = useState<LayoutItem[]>(() => loadJson(LS_KEY, null) || defaultLayout);

  useEffect(() => {
    const ids = new Set(layouts.map(l => l.i));
    const added = activeWidgets.filter(id => !ids.has(id));
    if (added.length > 0) {
      const maxY = layouts.reduce((m, l) => Math.max(m, l.y + l.h), 0);
      let x = 0;
      const items = added.map(id => {
        const def = WIDGETS.find(w => w.id === id);
        const w = def?.defaultW || 4, h = def?.defaultH || 3;
        if (x + w > 12) x = 0;
        const item: LayoutItem = { i: id, x, y: maxY, w, h, minW: 3, minH: 2 };
        x += w; return item;
      });
      setLayouts(prev => [...prev.filter(l => activeWidgets.includes(l.i)), ...items]);
    } else {
      setLayouts(prev => prev.filter(l => activeWidgets.includes(l.i)));
    }
  }, [activeWidgets]);

  const addWidget = (id: string) => { const next = [...activeWidgets, id]; setActiveWidgets(next); save(LS_WIDGETS, next); };
  const removeWidget = (id: string) => { const next = activeWidgets.filter(w => w !== id); setActiveWidgets(next); save(LS_WIDGETS, next); };
  const resetLayout = () => {
    setActiveWidgets(DEFAULT_WIDGETS); save(LS_WIDGETS, DEFAULT_WIDGETS);
    localStorage.removeItem(LS_KEY); localStorage.removeItem(LS_MODES);
    setLayouts(defaultLayout); setWidgetModes({});
    notifications.show({ message: 'Layout reset', color: 'blue' });
  };

  const available = WIDGETS.filter(w => !activeWidgets.includes(w.id));

  return (
    <div style={{ padding: '16px 24px' }}>
      <Group justify="space-between" mb="md" align="flex-start">
        <div>
          <Group gap="xs" align="center">
            <ThemeIcon size="md" variant="light" color="var(--color-primary-700)" radius="md"><IconLayoutDashboard size={16} /></ThemeIcon>
            <Title order={3} c="black">Common Operating Picture</Title>
          </Group>
          <Text size="sm" c="dimmed" mt={2}>{activeIncident?.name || 'No incident'} — Live overview · 30s refresh</Text>
        </div>
        <Group gap="xs">
          <Button size="xs" variant="subtle" leftSection={<IconRefresh size={14} />} onClick={loadAllData}>Refresh</Button>
          {available.length > 0 && (
            <Menu shadow="md" width={220}>
              <Menu.Target><Button size="xs" variant="light" leftSection={<IconPlus size={14} />}>Add Widget</Button></Menu.Target>
              <Menu.Dropdown>{available.map(w => (<Menu.Item key={w.id} leftSection={<w.icon size={14} />} onClick={() => addWidget(w.id)}>{w.title}</Menu.Item>))}</Menu.Dropdown>
            </Menu>
          )}
          <Button size="xs" variant={isEditing ? 'filled' : 'light'} color={isEditing ? 'orange' : 'gray'}
            leftSection={<IconSettings size={14} />} onClick={() => setIsEditing(!isEditing)}>
            {isEditing ? 'Done' : 'Edit'}
          </Button>
          <Tooltip label="Reset"><ActionIcon size="sm" variant="subtle" onClick={resetLayout}><IconMaximize size={14} /></ActionIcon></Tooltip>
        </Group>
      </Group>

      <RGLComponent
        layouts={{ lg: layouts }} breakpoints={{ lg: 1200, md: 900, sm: 600 }}
        cols={{ lg: 12, md: 8, sm: 4 }} rowHeight={60} width={1200}
        isDraggable={isEditing} isResizable={isEditing}
        onLayoutChange={(layout: any) => { setLayouts(layout); save(LS_KEY, layout); }}
        draggableHandle=".cop-drag" compactType="vertical"
      >
        {activeWidgets.map(id => {
          const def = WIDGETS.find(w => w.id === id);
          const Comp = WIDGET_MAP[id];
          if (!def || !Comp) return null;
          const currentMode = widgetModes[id] || (def.modes?.[0] ?? 'stat');
          const hasModes = def.modes && def.modes.length > 1;
          return (
            <div key={id}>
              <Paper p="sm" h="100%" withBorder radius="md"
                style={{ display: 'flex', flexDirection: 'column', borderTop: `3px solid ${def.color}`, overflow: 'hidden', background: 'white' }}>
                <Group justify="space-between" mb={6} wrap="nowrap">
                  <Group gap={6} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                    {isEditing && <div className="cop-drag" style={{ cursor: 'grab', display: 'flex' }}><IconGripVertical size={12} color="#adb5bd" /></div>}
                    <Text size="xs" fw={700} c="dimmed" tt="uppercase" style={{ letterSpacing: 0.5 }} truncate>{def.title}</Text>
                  </Group>
                  <Group gap={4} wrap="nowrap">
                    {hasModes && (
                      <Menu shadow="sm" width={140} position="bottom-end">
                        <Menu.Target><ActionIcon size="xs" variant="subtle" color="gray"><IconChartBar size={12} /></ActionIcon></Menu.Target>
                        <Menu.Dropdown>
                          {def.modes.map(m => (
                            <Menu.Item key={m} onClick={() => setMode(id, m)}
                              style={{ fontWeight: currentMode === m ? 700 : 400, background: currentMode === m ? 'var(--mantine-color-blue-0)' : undefined }}>
                              {MODE_LABELS[m]}
                            </Menu.Item>
                          ))}
                        </Menu.Dropdown>
                      </Menu>
                    )}
                    {isEditing && <ActionIcon size="xs" variant="subtle" color="red" onClick={() => removeWidget(id)}><IconTrash size={10} /></ActionIcon>}
                  </Group>
                </Group>
                <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}><Comp mode={currentMode} /></div>
              </Paper>
            </div>
          );
        })}
      </RGLComponent>
    </div>
  );
}
